import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Sun, Moon, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSite } from "@/context/ThemeContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/courses", label: "Courses" },
  { to: "/activities", label: "Activities" },
  { to: "/gallery", label: "Gallery" },
  { to: "/achievements", label: "Achievements" },
  { to: "/events", label: "Events" },
  { to: "/admission", label: "Admission" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { light, toggleLight } = useSite();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong py-2 shadow-lg" : "py-4 bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto max-w-7xl px-5 flex items-center justify-between">
        <Link to="/" onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        <div className="hidden xl:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                  isActive ? "text-primary-c" : "text-t hover:text-primary-c"
                }`
              }
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a
            href="tel:9272980098"
            className="hidden md:inline-flex btn-ghost px-4 py-2.5 text-sm items-center gap-1.5"
            data-testid="nav-call"
          >
            <Phone size={15} /> 9272980098
          </a>
          <button
            onClick={() => navigate("/courses")}
            className="hidden lg:inline-flex btn-ghost px-4 py-2.5 text-sm"
            data-testid="explore-courses-nav"
          >
            Explore Courses
          </button>
          <button
            onClick={() => navigate("/admission")}
            className="hidden sm:inline-flex btn-primary px-5 py-2.5 text-sm"
            data-testid="apply-now-nav"
          >
            Apply Now
          </button>
          <button
            onClick={toggleLight}
            className="p-2 rounded-full btn-ghost"
            aria-label="Toggle theme"
            data-testid="theme-toggle"
          >
            {light ? <Moon size={18} /> : <Sun size={18} />}
          </button>
          <button
            className="xl:hidden p-2 rounded-full btn-ghost"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            data-testid="hamburger-menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="xl:hidden mt-2 mx-4 glass-strong rounded-2xl p-4 flex flex-col gap-1" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `px-4 py-3 rounded-xl text-base font-semibold ${isActive ? "text-primary-c bg-surface" : "text-t"}`
              }
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              onClick={() => { setOpen(false); navigate("/courses"); }}
              className="btn-ghost px-4 py-3"
              data-testid="explore-courses-mobile"
            >
              Explore Courses
            </button>
            <button
              onClick={() => { setOpen(false); navigate("/admission"); }}
              className="btn-primary px-4 py-3"
              data-testid="apply-now-mobile"
            >
              Apply Now
            </button>
          </div>
          <a
            href="tel:9272980098"
            className="btn-ghost px-4 py-3 mt-2 inline-flex items-center justify-center gap-2"
            data-testid="call-mobile"
          >
            <Phone size={16} /> Call 9272980098
          </a>
        </div>
      )}
    </header>
  );
}
